<template>
    <ValidationProvider
        v-slot="{errors, classes, passed, failed}"
        tag="div"
        :class="['text-input', ...additionalClasses, {'mt-24': externalLabel}]"
        :rules="rules"
        :name="name"
        :mode="validationMode"
    >
        <label
            :class="[
                'field-label',
                {'focused': isFocused, 'external': externalLabel},
                ...labelClasses
            ]"
            :for="`${name}_input`"
        >
            {{ label }}
            <slot name="label" />
            <sup
                v-if="isRequiredField"
                class="text-red"
            >
                *
            </sup>
        </label>
        <slot
            name="icon"
            :state="getCurrentFieldState({passed, failed})"
        />
        <input
            :id="`${name}_input`"
            v-mask:[regexMask]="{mask: mask || regexMask, revalidate}"
            :value="value"
            :class="[
                'field-input',
                {
                    'outlined': outlined,
                    'disabled': disabled,
                    'external': externalLabel,
                },
                ...inputClasses,
                classes
            ]"
            type="text"
            :name="name"
            :disabled="disabled"
            :autocomplete="autocomplete"
            :role="autocomplete === 'off' ? 'presentation' : ''"
            :maxlength="maxlength"
            :data-is-mask-placeholder-disabled="disableMaskPlaceholder"
            :placeholder="maskPlaceholder"
            :inputmode="inputmode"
            @focus="focusHandler"
            @blur="blurHandler"
            @change="$emit('change', $event)"
            @input="onInputHandler"
            @keydown="$emit('keydown', $event)"
            @keyup="$emit('keyup', $event)"
            @keypress="$emit('keypress', $event)"
        >
        <span
            :class="[
                'field-error',
                ...errorClasses
            ]"
        >
            {{ errors[0] }}
        </span>
    </ValidationProvider>
</template>

<script>
import {computed, defineComponent, ref, watch} from 'vue';

import mask from '@/core/directives/maskDerective';
import FORM_STATES from '@/core/constants/formStates';
import '@/assets/styles/ui-elements/textInput.scss';

export default defineComponent({
    name: 'TextInput',
    directives: {
        mask,
    },
    props: {
        value: {
            type: [String, Number, Boolean],
            required: true,
        },
        name: {
            type: String,
            default: () => '',
        },
        label: {
            type: String,
            default: () => '',
        },
        additionalClasses: {
            type: Array,
            default: () => ([]),
        },
        inputClasses: {
            type: Array,
            default: () => ([]),
        },
        labelClasses: {
            type: Array,
            default: () => ([]),
        },
        errorClasses: {
            type: Array,
            default: () => ([]),
        },
        outlined: {
            type: Boolean,
            default: () => false,
        },
        disabled: {
            type: Boolean,
            default: () => false,
        },
        rules: {
            type: [String, Object],
            default: () => '',
        },
        autocomplete: {
            type: String,
            default: () => 'off',
        },
        uppercase: {
            type: Boolean,
            default: () => false,
        },
        capitalize: {
            type: Boolean,
            default: () => false,
        },
        capitalizeAll: {
            type: Boolean,
            default: () => false,
        },
        maxlength: {
            type: [String, Number],
            default: () => 1000,
        },
        numberInput: {
            type: Boolean,
            default: () => false,
        },
        validationMode: {
            type: String,
            default: () => 'eager',
        },
        maskPlaceholder: {
            type: String,
            default: () => '',
        },
        externalLabel: {
            type: Boolean,
            default: () => false,
        },
        inputmode: {
            type: String,
            default: () => 'text',
        },
        revalidate: {
            type: Boolean,
            default: () => false,
        },
        mask: {
            type: String,
            default: () => '',
        },
        regexMask: {
            type: String,
            default: () => '',
        },
        suggessions: {
            type: Array,
            default: () => [],
        },
        disableMaskPlaceholder: {
            type: Boolean,
            default: () => false,
        },
    },
    emits: ['input', 'change', 'focus', 'blur', 'keydown', 'keyup', 'keypress'],
    setup(props, context) {
        const isFocused = ref(false);
        const fieldState = ref(FORM_STATES.INITIAL);

        const isRequiredField = computed(() => {
            if (!props.rules) {
                return false;
            }

            return typeof props.rules === 'string'
                ? props.rules.includes('required')
                : Object.keys(props.rules).includes('required');
        });

        function focusHandler(event) {
            fieldState.value = FORM_STATES.FOCUS;
            isFocused.value = true;
            context.emit('focus', event);
        }

        function blurHandler(event) {
            if (fieldState.value === FORM_STATES.FOCUS) {
                fieldState.value = FORM_STATES.INITIAL;
            }

            if (!props.value) {
                isFocused.value = false;
            }

            context.emit('blur', event);
        }

        function getCurrentFieldState({passed, failed}) {
            if (passed) {
                fieldState.value = FORM_STATES.SUCCESS;
            }

            if (failed) {
                fieldState.value = FORM_STATES.ERROR;
            }

            return fieldState.value;
        }

        function capitalizeString(str) {
            return str.charAt(0).toUpperCase() + str.slice(1);
        }

        function capitalizeAllWord(str) {
            const lowerStr = str.trim().toLowerCase();

            return lowerStr.replace(/^([а-яё])|([\s-_|,.=+/\\?~!@#$%^&*()`])+([а-яё])/g, (char) => {
                return char.toUpperCase();
            });
        }

        function onInputHandler(event) {
            const val = event.target.value;
            const value = props.numberInput ? val.trim().replace(/[^0-9]/g, '') : val;

            if (props.uppercase) {
                context.emit('input', value.toUpperCase());

                return;
            }

            if (props.capitalize) {
                context.emit('input', capitalizeString(value));

                return;
            }

            if (props.capitalizeAll) {
                context.emit('input', capitalizeAllWord(value));

                return;
            }

            context.emit('input', value);
        }

        watch(() => props.value, (newVal) => {
            isFocused.value = !!newVal;
        }, {immediate: true});

        return {
            isFocused,
            isRequiredField,
            focusHandler,
            blurHandler,
            getCurrentFieldState,
            onInputHandler,
        };
    },
});
</script>
